import React, { useCallback, useEffect, useMemo, useState } from "react";
import { hashKey } from "@tanstack/react-query";
import type { RowSelectionState } from "@tanstack/react-table";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import { Bill } from "@common/types/bill";
import { FiltersTypes } from "@common/types/filters/general";
import { BillView } from "@common/types/views/billViews";
import { View } from "@common/types/views/views";
import { BillsViewContainer } from "@components/bills/views/billsView/BillsViewContainer";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { isBillProviderAccount } from "@helpers/bills";
import { noop } from "@common/helpers/function";
import type { PaginationType } from "@hooks/paginationMode";
import { restrictViewConfigColumns } from "../shared/taskTableView";
import { LockedTaskFilters } from "../Transactions/LockedTaskFilters";
import { BillsTaskTableProvider } from "./BillsTaskTableProvider";
import {
    buildBillsTaskQuery,
    getBillsLockedActiveFilters,
    toBillsOverlayFilters,
} from "./billsTaskFilters";
import { omitBillsTaskScope } from "./billsTaskView";
import { useBillsTaskViewConfig } from "./useBillsTaskViewConfig";

interface BillsTaskTableProps {
    view: BillView;
    onViewChange: (view: View) => void;
    onFiltersChange: FiltersChangeFn;
    availableFilters: FilterTabs[];
    /** Reset target of column settings. */
    defaultView: BillView;
    /** Restricts column settings to these columns (the task's own columns). */
    allowedColumnIds?: ReadonlySet<string>;
    /** Freezes the date and entity scope (selection previews, sticky tasks). */
    lockScope?: boolean;
    /** Hides the entity picker when the entity is chosen elsewhere. */
    hideEntityFilter?: boolean;
    /** Creator-set criteria of a sticky task, rendered as locked pills. */
    originalFilters?: FiltersTypes;
    stickyBillIds?: string[];
    /** Row click handler. Without it rows are not clickable (previews). */
    onBillClick?: (bill?: Bill) => void;
    selectedBillId?: string;
    /** Rendered next to the table, such as the selected bill's details. */
    billDetails?: React.ReactNode;
    emptyView: () => React.ReactNode;
    className?: string;
    tableClassName?: string;
    isFullPage?: boolean;
    /** Numbered pages by default; the task body loads more on scroll. */
    paginationType?: PaginationType;
}

/**
 * Bills table shared by the task body and the task creation preview.
 * Persistence of view and filter changes is left to the caller; this component
 * only owns the query, the selection and the filter surface.
 */
export const BillsTaskTable: React.FC<BillsTaskTableProps> = ({
    view,
    onViewChange,
    onFiltersChange,
    availableFilters,
    defaultView,
    allowedColumnIds,
    lockScope = false,
    hideEntityFilter = false,
    originalFilters,
    stickyBillIds,
    onBillClick = noop,
    selectedBillId,
    billDetails,
    emptyView,
    className,
    tableClassName,
    isFullPage = true,
    paginationType,
}) => {
    const { hasAnyAccrualLedgers, viewConfig } = useBillsTaskViewConfig();
    const columnSettingsConfig = useMemo(
        () =>
            restrictViewConfigColumns({
                config: viewConfig,
                allowedColumnIds,
            }),
        [viewConfig, allowedColumnIds],
    );

    const query = useMemo(
        () =>
            buildBillsTaskQuery({
                viewFilters: view.filters,
                originalFilters,
                stickyBillIds,
            }),
        [view.filters, originalFilters, stickyBillIds],
    );

    // A changed query can load a different row set; a stale selection would
    // target bills the user can no longer see.
    const queryKey = hashKey([query]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    useEffect(() => {
        setRowSelection({});
    }, [queryKey]);
    const clearSelection = useCallback(() => setRowSelection({}), []);

    const overlayFilters = useMemo(
        () => toBillsOverlayFilters(view.filters),
        [view.filters],
    );
    const lockedActiveFilters = useMemo(
        () => getBillsLockedActiveFilters(originalFilters),
        [originalFilters],
    );

    const dateRange = useMemo(
        () => ({
            start: view.filters.periodStart,
            end: view.filters.periodEnd,
        }),
        [view.filters.periodStart, view.filters.periodEnd],
    );

    return (
        <>
            <TablePageHeader
                search={view.filters.search ?? ""}
                isFullPage={isFullPage}
                onSearchChange={(value) =>
                    onFiltersChange({ search: value || undefined })
                }
                className={className}
                filters={overlayFilters}
                onFiltersChange={onFiltersChange}
                availableFilters={availableFilters}
                accountFilterPredicate={isBillProviderAccount}
                counterpartyEntityLabel="Vendor"
                hideClearButton
                afterFiltersChildren={
                    originalFilters ? (
                        <LockedTaskFilters
                            lockedActiveFilters={lockedActiveFilters}
                            baseFilters={omitBillsTaskScope(originalFilters)}
                            maxVisibleFilters={lockedActiveFilters.length}
                        />
                    ) : undefined
                }
            >
                <DateRangeFilterNext
                    dateRange={dateRange}
                    disabled={lockScope}
                    onChange={(value) =>
                        onFiltersChange({
                            periodStart: value.start,
                            periodEnd: value.end,
                        })
                    }
                />
                {!hideEntityFilter && (
                    <EntityFilterCombobox
                        filters={{ entityIds: view.filters.entityIds }}
                        onChange={({ entityIds: nextEntityIds }) =>
                            onFiltersChange({ entityIds: nextEntityIds })
                        }
                        onlyBusiness
                        excludeAllMockEntities
                        disabled={lockScope}
                    />
                )}
                <ColumnSettings
                    view={view}
                    viewConfig={columnSettingsConfig}
                    defaultView={defaultView}
                    onViewChange={onViewChange}
                />
            </TablePageHeader>
            <div className={tableClassName}>
                <BillsTaskTableProvider
                    filters={query}
                    paginationType={paginationType}
                >
                    <BillsViewContainer
                        view={view}
                        onViewChange={onViewChange}
                        onBillClick={onBillClick}
                        selectedBillId={selectedBillId}
                        hasAnyAccrualLedgers={hasAnyAccrualLedgers}
                        rowSelection={rowSelection}
                        onRowSelectionChange={setRowSelection}
                        onClearSelection={clearSelection}
                        hasConnectedBillAccounts
                        onClearFilters={noop}
                        emptyView={emptyView}
                    />
                </BillsTaskTableProvider>
                {billDetails}
            </div>
        </>
    );
};
